import axios, {AxiosResponse} from 'axios';
import {MediaAlbum} from '../backend/enums/media-album';
import {MediaType} from '../backend/enums/media-type';
import {StorageFileType} from '../backend/enums/storage-file-type';
import {StorageType} from '../backend/enums/storage-type';
import {Media} from '../backend/interfaces/media';
import {StorageRequestResult} from '../backend/interfaces/storage-request';
import {PostStorageRequest} from '../backend/requests/post-storage-request';
import {BackendApiManager} from '../managers/backend-api';

interface MediaStorageRequestData {
  seniorUserId: string;
}

const storageUploadClient = axios.create();

export class MediaRepository {
  // Singleton instance
  private static _instance: MediaRepository;

  static get instance(): MediaRepository {
    if (!this._instance) {
      this._instance = new MediaRepository();
    }
    return this._instance;
  }

  private expectStatus(status: number, expectedStatus: number, operation: string): void {
    if (status !== expectedStatus) {
      throw new Error(
        `${operation} returned unexpected status ${status}; expected ${expectedStatus}.`
      );
    }
  }

  public async get(
    seniorUserId: string,
    limit: number,
    skip: number,
    signal?: AbortSignal,
    skipDate?: Date
  ): Promise<{data: Array<Media>; totalCount: number; rawCount: number}> {
    const query = new URLSearchParams({
      seniorUserId,
      album: MediaAlbum.Shared,
      limit: String(limit),
      skip: String(skip),
    });

    if (skipDate) {
      query.set('skipDate', skipDate.toISOString());
    }

    query.append('types', MediaType.Photo);
    query.append('types', MediaType.Video);

    const response: AxiosResponse<Array<Media>> = await BackendApiManager.instance.get<
      Array<Media>
    >(`/media?${query.toString()}`, {signal});
    this.expectStatus(response.status, 200, 'Media list request');
    const headerTotalCount = Number.parseInt(response.headers['x-total-count'] || '', 10);
    const rawCount = response.data.length;

    return {
      data: response.data,
      totalCount: Number.isNaN(headerTotalCount) ? rawCount : headerTotalCount,
      rawCount,
    };
  }

  public async getById(id: string, signal?: AbortSignal): Promise<Media> {
    const response: AxiosResponse<Media> = await BackendApiManager.instance.get<Media>(
      `/media/${id}`,
      {signal}
    );
    this.expectStatus(response.status, 200, 'Media detail request');

    return response.data;
  }

  public async requestMediaUpload(
    seniorUserId: string,
    type: MediaType.Photo | MediaType.Video,
    fileType:
      | StorageFileType.JPG
      | StorageFileType.PNG
      | StorageFileType.GIF
      | StorageFileType.MP4
      | StorageFileType.MOV
      | StorageFileType.WebMVideo
  ): Promise<StorageRequestResult<MediaStorageRequestData>> {
    const body: PostStorageRequest<MediaStorageRequestData> = {
      type: type === MediaType.Photo ? StorageType.Photo : StorageType.Video,
      fileType,
      data: {seniorUserId},
    };
    const response: AxiosResponse<StorageRequestResult<MediaStorageRequestData>> =
      await BackendApiManager.instance.post<StorageRequestResult<MediaStorageRequestData>>(
        '/storage/request',
        body,
        {retryOnBadGateway: false}
      );
    this.expectStatus(response.status, 201, 'Media storage request');

    return response.data;
  }

  public async uploadMedia(
    storageRequest: StorageRequestResult<MediaStorageRequestData>,
    file: Blob
  ): Promise<string> {
    const {uploadUrl, uploadContentTypeHeader, linkingUrl} = storageRequest;

    if (!uploadUrl || !uploadContentTypeHeader || !linkingUrl) {
      throw new Error('Media storage request did not include the required upload details.');
    }

    const response: AxiosResponse<void> = await storageUploadClient.put<void>(uploadUrl, file, {
      headers: {
        'Content-Type': uploadContentTypeHeader,
        'Cache-Control': 'public, max-age=31536000',
      },
    });

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`Media upload failed with status ${response.status}.`);
    }

    return linkingUrl;
  }

  public async createMedia(
    seniorUserId: string,
    type: MediaType.Photo | MediaType.Video,
    linkingUrl: string,
    description?: string
  ): Promise<Media> {
    const trimmedDescription = description?.trim();
    const body = {
      seniorUserId,
      type,
      album: MediaAlbum.Shared,
      url: linkingUrl,
      ...(trimmedDescription ? {description: trimmedDescription} : {}),
    };
    const response: AxiosResponse<Media> = await BackendApiManager.instance.post<Media>(
      '/media',
      body,
      {retryOnBadGateway: false}
    );
    this.expectStatus(response.status, 201, 'Media creation');

    return response.data;
  }

  public async updateDescription(id: string, description: string | null): Promise<Media> {
    const trimmedDescription = description?.trim() || null;
    const response: AxiosResponse<Media> = await BackendApiManager.instance.patch<Media>(
      `/media/${id}`,
      {description: trimmedDescription},
      {retryOnBadGateway: false}
    );
    this.expectStatus(response.status, 200, 'Media description update');

    return response.data;
  }

  public async deleteById(id: string): Promise<204 | 205> {
    const response: AxiosResponse<void> = await BackendApiManager.instance.delete<void>(
      `/media/${id}`,
      {retryOnBadGateway: false}
    );

    if (response.status !== 204 && response.status !== 205) {
      throw new Error(`Media delete returned unexpected status ${response.status}.`);
    }

    return response.status;
  }
}
